import { normalizeCnpj } from '@/@core/utils/cnpj.utils';

interface Person {
    id: number;
    rg?: string;
    cpf: string;
    area?: string;
    name: string;
    type?: string;
    email?: string;
    gender?: string;
    access_id?: string;
    created_at: string;
    occupation?: string;
    updated_at: string;
    nationality?: string;
    has_children?: boolean;
    birthday_date?: string;
    image_upload_id?: string;
    juridical_person_id?: string;
    rne?: string;
}

interface Address {
    id: number;
    city: string;
    state: string;
    number: string;
    street: string;
    zipcode: string;
    complement?: string;
    created_at: string;
    updated_at: string;
    neighborhood: string;
    additional_details?: string;
}

export interface SearchCustomerInterface {
    payment_customer: {
        id: number;
        person: Person;
        address: Address;
    }
}


export type GetRelated = {
    scheduleIds: number[];
    invoiceNumbers: string[];
};

export interface PaymentCustomer {
    id?: number;
    name?: string;
    email?: string;
    nationality?: string;
    registry_code?: string;
    rne?: string;
}

/**
 * Compara se dois customers são iguais baseado em regras de negócio específicas:
 * 1. Nacionalidade deve ser igual
 * 2. Se ambos têm CPF, devem ser iguais
 * 3. Se ambos têm RNE, devem ser iguais  
 * 4. Se ambos têm email, devem ser iguais
 * 5. Se ambos têm nome, devem ser iguais
 * 
 * @param customer1 Primeiro customer para comparação
 * @param customer2 Segundo customer para comparação
 * @returns true se os customers são considerados iguais, false caso contrário
 */
export const areCustomersEqual = (customer1: PaymentCustomer, customer2: PaymentCustomer): boolean => {
    // 1. Primeira verificação: nacionalidades diferentes = customers diferentes
    const nationality1 = customer1?.nationality?.toUpperCase();
    const nationality2 = customer2?.nationality?.toUpperCase();
    
    if (nationality1 !== nationality2) {
        return false;
    }
    
    // 2. Se nacionalidades são iguais, verificar CPF/CNPJ (preserva letras para CNPJ alfanumérico)
    const cpf1 = normalizeCnpj(customer1?.registry_code ?? '');
    const cpf2 = normalizeCnpj(customer2?.registry_code ?? '');
    
    // Se ambos têm CPF, devem ser iguais
    if (cpf1 && cpf2) {
        if (cpf1 !== cpf2) {
            return false;
        }
    }
    // Se apenas um tem CPF, são diferentes
    else if ((cpf1 && !cpf2) || (!cpf1 && cpf2)) {
        return false;
    }
    
    // 3. Se ambos têm RNE, devem ser iguais
    const rne1 = customer1?.rne?.trim();
    const rne2 = customer2?.rne?.trim();
    
    if (rne1 && rne2) {
        if (rne1 !== rne2) {
            return false;
        }
    }
    // Se apenas um tem RNE, são diferentes
    else if ((rne1 && !rne2) || (!rne1 && rne2)) {
        return false;
    }
    
    // 4. Verificar email
    const email1 = customer1?.email?.trim().toLowerCase();
    const email2 = customer2?.email?.trim().toLowerCase();
    
    if (email1 && email2) {
        if (email1 !== email2) {
            return false;
        }
    }
    // Se apenas um tem email, são diferentes
    else if ((email1 && !email2) || (!email1 && email2)) {
        return false;
    }
    
    // 5. Verificar nome
    const name1 = customer1?.name?.trim().toLowerCase();
    const name2 = customer2?.name?.trim().toLowerCase();
    
    if (name1 && name2) {
        if (name1 !== name2) {
            return false;
        }
    }
    // Se apenas um tem nome, são diferentes
    else if ((name1 && !name2) || (!name1 && name2)) {
        return false;
    }
    
    return true;
};